import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { PRAIRI_SURFACES } from '../../constants/prairiSurfaces';
import { companyNameUnlessSameAsPerson } from '../../utils/companyNameUnlessSameAsPerson';
import shareIconGrey from '../../assets/share_icon_grey2.png';

/** Existing PRAIRI link cyan (SignUp / VerifyEmail) — matches “Share” accent in Peter’s mock */
const SHARE_ACCENT = '#00b0f0';
/** Inactive header grey — Close */
const MUTED_GREY = '#c8c8c8';
const INPUT_BG = '#F2F2F2';

interface ShareStartupPopupProps {
  isOpen: boolean;
  startupName: string;
  startupId: string;
  onClose: () => void;
  /** Called after a successful share — parent shows a disappearing toast. */
  onShared?: (info: { recipientName: string }) => void;
}

const ShareStartupPopup: React.FC<ShareStartupPopupProps> = ({
  isOpen,
  startupName,
  startupId,
  onClose,
  onShared,
}) => {
  const { user } = useAuth();
  const { investorProfile } = useProfile();
  const senderName = user?.name?.trim() || '';
  const senderCompany = companyNameUnlessSameAsPerson(investorProfile?.companyName, senderName);
  const fromLine = [senderName, senderCompany].filter(Boolean).join(' ');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const messagePreview = [
    `Hey, here's an interesting startup called ${startupName.trim() || 'this startup'} on the PRAIRI platform. Thought you might be interested.`,
    ...(fromLine ? ['', `From: ${fromLine}`] : []),
  ].join('\n');

  useEffect(() => {
    if (!isOpen) {
      setRecipientName('');
      setRecipientEmail('');
      setPhonePrefix('');
      setPhoneNumber('');
      setError(null);
      setSending(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, sending, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = recipientName.trim();
    const email = recipientEmail.trim().toLowerCase();
    if (!name) {
      setError('Please enter their name.');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    const prefix = phonePrefix.trim();
    const number = phoneNumber.trim();
    const recipientPhone =
      prefix || number ? `${prefix}${prefix && number ? ' ' : ''}${number}`.trim() : undefined;

    setSending(true);
    setError(null);
    try {
      const shareFn = httpsCallable(functions, 'shareStartupProfile');
      const result = await shareFn({
        startupId,
        recipientName: name,
        recipientEmail: email,
        recipientPhone,
      });
      const data = result.data as { success?: boolean; error?: string };
      if (!data?.success) {
        setError(data?.error || 'Failed to share profile.');
        setSending(false);
        return;
      }
      setSending(false);
      onShared?.({ recipientName: name });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share profile.');
      setSending(false);
    }
  };

  const inputClassName =
    'w-full px-2 py-0.5 rounded-md text-xs text-gray-900 border-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60';
  const inputStyle = {
    backgroundColor: INPUT_BG,
    borderColor: PRAIRI_SURFACES.divider,
  } as const;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/70 px-4"
      onClick={() => {
        if (!sending) onClose();
      }}
    >
      <div
        className="w-full max-w-[26rem] rounded-2xl border border-[#444444] px-4 pt-1 pb-5 shadow-2xl sm:px-5 sm:pt-1.5 sm:pb-6"
        style={{ backgroundColor: PRAIRI_SURFACES.profileCard, fontFamily: 'Rubik, sans-serif' }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-startup-title"
      >
        <form onSubmit={handleSubmit} className="space-y-2 text-left">
          <div className="flex items-center justify-between">
            <img src={shareIconGrey} alt="" className="h-8 w-8 object-contain" aria-hidden />
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="text-sm underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
              style={{ color: MUTED_GREY }}
            >
              Close
            </button>
          </div>

          <h2 id="share-startup-title" className="sr-only">
            Share startup
          </h2>

          <div className="!mt-4 space-y-1 text-sm leading-snug text-white">
            <p>Send this startup to an investor colleague who might be interested.</p>
            <p>
              The profile will be viewable to them{' '}
              <span className="font-bold">without having to join PRAIRI</span> (or, of course, you
              can{' '}
              <Link
                to="/investors/pricing"
                className="underline underline-offset-2 hover:text-gray-200"
                onClick={onClose}
              >
                invite them
              </Link>
              )
            </p>
          </div>

          <p className="!mt-5 text-sm font-semibold" style={{ color: SHARE_ACCENT }}>
            Share to:
          </p>

          <div className="flex items-center gap-3">
            <label
              htmlFor="share-recipient-name"
              className="w-[5.75rem] flex-shrink-0 text-sm text-white sm:w-28"
            >
              Their Name <span className="text-white">*</span>
            </label>
            <input
              id="share-recipient-name"
              type="text"
              value={recipientName}
              onChange={e => {
                setRecipientName(e.target.value);
                setError(null);
              }}
              disabled={sending}
              className={inputClassName}
              style={inputStyle}
              autoComplete="name"
            />
          </div>

          <div className="flex items-center gap-3">
            <label
              htmlFor="share-recipient-email"
              className="w-[5.75rem] flex-shrink-0 text-sm text-white sm:w-28"
            >
              Their Email <span className="text-white">*</span>
            </label>
            <input
              id="share-recipient-email"
              type="email"
              value={recipientEmail}
              onChange={e => {
                setRecipientEmail(e.target.value);
                setError(null);
              }}
              disabled={sending}
              className={inputClassName}
              style={inputStyle}
              autoComplete="email"
            />
          </div>

          <div className="flex items-center gap-3">
            <label
              htmlFor="share-recipient-phone"
              className="w-[5.75rem] flex-shrink-0 text-sm text-white sm:w-28"
            >
              Their Phone
            </label>
            <div className="flex min-w-0 flex-1 gap-2">
              <input
                id="share-recipient-phone-prefix"
                type="text"
                inputMode="tel"
                value={phonePrefix}
                onChange={e => setPhonePrefix(e.target.value)}
                disabled={sending}
                aria-label="Phone country prefix"
                className={`${inputClassName} !w-12 shrink-0`}
                style={inputStyle}
                autoComplete="tel-country-code"
              />
              <input
                id="share-recipient-phone"
                type="tel"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                disabled={sending}
                aria-label="Phone number"
                className={`${inputClassName} min-w-0 flex-1`}
                style={inputStyle}
                autoComplete="tel-national"
              />
            </div>
          </div>

          <div className="!mt-5 flex flex-col items-center pt-2">
            <label
              htmlFor="share-message-preview"
              className="mb-1 block w-[78%] text-left text-sm text-white"
            >
              Message:
            </label>
            <textarea
              id="share-message-preview"
              readOnly
              value={messagePreview}
              rows={5}
              className="w-[78%] resize-none rounded-2xl border-2 px-3 py-1.5 text-sm leading-snug text-gray-900 focus:outline-none"
              style={inputStyle}
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex justify-center pt-2 pb-0">
            <button
              type="submit"
              disabled={sending}
              className="rounded-full border-2 border-emerald-500 bg-transparent px-8 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/5 disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ShareStartupPopup;
